import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';

interface WeeklyPatternData {
    readonly day_num: number;
    readonly day_name: string;
    readonly avg_contracts: number;
    readonly avg_revenue: number;
}

interface WeeklyPatternChartProps {
    readonly data: readonly WeeklyPatternData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function WeeklyPatternChart({
    data,
    loading = false,
    currency = 'EUR',
}: WeeklyPatternChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currencyLabel = currency === 'LEI' ? 'RON' : 'EUR';

    // Sort by day number and map day names
    const sortedData = [...data].sort((a, b) => a.day_num - b.day_num);
    const categories = sortedData.map(d => d.day_name);

    // Calculate the average to highlight above/below average days
    const avgContracts =
        sortedData.reduce((sum, d) => sum + d.avg_contracts, 0) / sortedData.length;

    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            height: 300,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories,
            title: {
                text: t('statistics.advanced.dayOfWeek', 'Day of Week'),
            },
        },
        yAxis: [
            {
                title: {
                    text: t('statistics.advanced.avgContracts', 'Avg Contracts'),
                },
                min: 0,
                plotLines: [
                    {
                        value: avgContracts,
                        color: '#ff4d4f',
                        dashStyle: 'Dash',
                        width: 2,
                        label: {
                            text: t('statistics.advanced.weeklyAverage', 'Weekly Average'),
                            align: 'right',
                            style: {
                                color: '#ff4d4f',
                                fontSize: '10px',
                            },
                        },
                    },
                ],
            },
            {
                title: {
                    text:
                        t('statistics.advanced.avgRevenue', 'Avg Revenue') + ` (${currencyLabel})`,
                },
                opposite: true,
            },
        ],
        tooltip: {
            shared: true,
            valueDecimals: 1,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                colorByPoint: true,
                colors: sortedData.map(d =>
                    d.avg_contracts >= avgContracts ? '#52c41a' : '#faad14',
                ),
            },
        },
        series: [
            {
                type: 'column',
                name: t('statistics.advanced.avgContracts', 'Avg Contracts'),
                data: sortedData.map(d => d.avg_contracts),
                yAxis: 0,
            },
            {
                type: 'spline',
                name: t('statistics.advanced.avgRevenue', 'Avg Revenue'),
                data: sortedData.map(d => d.avg_revenue),
                color: '#1890ff',
                yAxis: 1,
                marker: {
                    enabled: true,
                    radius: 4,
                },
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default WeeklyPatternChart;
